import { Injectable } from '@angular/core';
import {
  ProductListGetProductsRequestDto,
  ProductListGetProductsResponseDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { ProductService } from '@epicuro-next/data/product';
import { chunkArray } from '@epicuro-next/utilities';
import {
  Product,
  ProductReferenceInput,
  SearchQuery,
  SearchSortType,
} from 'graphql-api/gql-api-interfaces';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { mapProductsByReferenceOrder, mapProductsQuery } from '../../utils';

@Injectable()
export class ProductListProductsApiService {
  constructor(private productsService: ProductService) {}

  public getAllProducts(
    query: SearchQuery,
  ): Observable<ProductListGetProductsResponseDto> {
    if (!query.productReferences?.length) {
      return of({
        products: [],
        resultCount: 0,
      });
    }
    return forkJoin(
      chunkArray(query.productReferences, 20).map((productReferences) => {
        return this.productsService.productsByReferences({
          ...query,
          start: 0,
          num: productReferences.length,
          productReferences,
        });
      }),
    ).pipe(
      map((responses) => responses.flatMap(({ products }) => products ?? [])),
      map((products) => ({
        products,
        resultCount: products.length,
      })),
    );
  }

  public getProducts({
    query,
  }: ProductListGetProductsRequestDto): Observable<ProductListGetProductsResponseDto> {
    if (!query.productReferences?.length) {
      return of({
        products: [],
        resultCount: 0,
      });
    }

    const { accountId } = query;
    const { payloadQuery, toSortByOrder } = mapProductsQuery(query);
    const { site, productReferences } = payloadQuery;

    return this.productsService
      .checkProductsAvailabilityByReferences(accountId, {
        productReferences,
        siteId: site,
      })
      .pipe(
        map((productsAvailability) => {
          let products = [];
          if (query.available) {
            products = productsAvailability.availableProductReferences;
          } else {
            products = productsAvailability.unavailableProductReferences;
          }

          return toSortByOrder
            ? mapProductsByReferenceOrder(products, productReferences)
            : products;
        }),
        mergeMap((items) => {
          const num = query.num ?? 10;
          let itemsToRequest: ProductReferenceInput[];
          if (payloadQuery.sortField !== SearchSortType.Category) {
            const sliceEnd =
              query.start + num > items.length ? undefined : query.start + num;
            itemsToRequest = items.slice(
              query.start,
              sliceEnd,
            ) as ProductReferenceInput[];

            if (!itemsToRequest.length) {
              return of({
                products: [],
                resultCount: 0,
              });
            }
            payloadQuery.start = 0;
          } else {
            itemsToRequest = items as ProductReferenceInput[];
            payloadQuery.start = query.start;
            payloadQuery.num = num;
          }

          return this.productsService
            .productsByReferences({
              ...payloadQuery,
              productReferences: itemsToRequest,
            })
            .pipe(
              map((productsByReferences) => ({
                products: (productsByReferences?.products as Product[]) ?? [],
                resultCount: items?.length ?? 0,
              })),
            );
        }),
      );
  }
}
